import Cookies from 'vue-cookies';

import { useHostname } from '@bv/composables/common';

// Configuration
const IS_HTTPS: boolean = window.location.protocol === 'https';
const SAME_SITE: SameSite = IS_HTTPS ? 'None' : 'Lax';
const DEFAULT_PATH: string = '/';

/**
 * Defines the possible values for the SameSite cookie attribute, which controls
 * how cookies are sent with cross-site requests to prevent CSRF attacks.
 */
type SameSite = 'None' | 'Lax' | 'Strict';

/**
 * Defines the shape of the configuration object.
 */
export interface ConfigOptions {
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: SameSite;
}

/**
 * Composable for managing browser cookies.
 * It automatically handles JSON serialization for objects and arrays.
 */
export const useCookies = () => {
  // Use a shorter alias for convenience
  const cookies = Cookies as unknown as typeof Cookies.VueCookies;

  /**
   * Configures the default settings for all subsequent cookie operations.
   * @param {ConfigOptions} options - The configuration options to apply.
   */
  const config = (options: ConfigOptions = {}): void => {
    const { domain: DOMAIN } = useHostname();

    const path = options.path ?? DEFAULT_PATH;
    const domain = options.domain ?? `.${DOMAIN}`;
    const secure = options.secure ?? IS_HTTPS;
    const sameSite = options.sameSite ?? SAME_SITE;

    cookies.config('', path, domain, secure, sameSite);
  };

  /**
   * Retrieves and parses a value from a cookie.
   * @param key The key of the cookie to retrieve.
   * @returns The parsed value, or null if the cookie doesn't exist.
   */
  const get = <T = any>(key: string): T | null => {
    const value = cookies.get(key);
    if (value === null || value === undefined) {
      return null;
    }

    // Try to parse it as JSON, if it fails, return the raw value
    try {
      return JSON.parse(value) as T;
    } catch {
      return value as T;
    }
  };

  /**
   * Sets a value in a cookie. Objects and arrays are automatically stringified.
   * @param {string} key The key of the cookie to set.
   * @param {any} value The value to store. If an object or array, it will be stringified.
   * @param {object} [options] - Optional configuration object, destructured to get expires.
   * @param {string | number | Date} [options.expires] - The expiration of the cookie (e.g., '7d', 7, or a Date object).
   * @returns {void}
   */
  const set = (key: string, value: any, { expires }: { expires?: string | number | Date } = {}): void => {
    const valueToStore = typeof value === 'object' && value !== null ? JSON.stringify(value) : value;

    cookies.set(key, valueToStore, expires);
  };

  /**
   * Removes a cookie. Uses the default domain unless overridden.
   * @param key The key of the cookie to remove.
   * @param path The path of the cookie. Defaults to '/'.
   * @param domain The domain of the cookie. Defaults to the configured domain.
   */
  const remove = (key: string, path: string = DEFAULT_PATH, domain?: string): void => {
    const { domain: DOMAIN } = useHostname();
    cookies.remove(key, path, domain || `.${DOMAIN}`);
  };

  /**
   * Removes all cookies for the configured domain.
   */
  const removeAll = (): void => {
    const { domain } = useHostname();

    cookies.keys().forEach((cookieName: string) => {
      remove(cookieName, DEFAULT_PATH, `.${domain}`);
    });
  };

  return {
    config,
    get,
    set,
    remove,
    removeAll,
  };
};
